/* eslint-disable no-shadow */
import { all, call, put, fork, takeEvery } from 'redux-saga/effects';
import { adminRoot } from '../../constants/defaultValues';
import { getCurrentUser, setCurrentUser } from '../../helpers/Utils';

import api, { clearAuthorizationHeader, LOGOUT_URL } from '../../services/api';
import { resetInstallationSettingsCache } from '../../services/installationSettings';

import { LOGIN_USER, LOGOUT_USER } from '../actions';

import { loginUserSuccess, loginUserError } from './actions';

export function* watchLoginUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOGIN_USER, loginWithEmailPassword);
}

// Sem `.catch`: o erro do axios chega ao `try/catch` da saga, que o converte em
// mensagem legível (F-10). Antes o objeto de erro era devolvido como se fosse
// a resposta e o toast mostrava "Request failed with status code 400".
const loginWithEmailPasswordAsync = (user, password) =>
  api
    .post('/auth_token/', {
      username: user,
      password,
    })
    .then((res) => res.data);

export const INVALID_CREDENTIALS_MESSAGE = 'Usuário ou senha inválidos.';
export const NETWORK_ERROR_MESSAGE =
  'Não foi possível conectar ao servidor. Verifique a conexão e tente novamente.';
export const SERVER_ERROR_MESSAGE =
  'Erro no servidor ao entrar. Tente novamente em instantes.';

// O DRF responde credencial errada com 400 (`non_field_errors`, ou erro por
// campo se faltar usuário/senha); 401/403 também significam "não autorizado".
// Um bloqueio por tentativas excessivas (S-05) é propositalmente igual a
// credencial inválida no backend. Sem `response` e com `request` = rede/CORS;
// qualquer outra coisa (5xx, resposta malformada) = erro do servidor.
export const loginErrorMessage = (error) => {
  const response = error && error.response;
  if (!response) {
    return error && error.request
      ? NETWORK_ERROR_MESSAGE
      : SERVER_ERROR_MESSAGE;
  }
  const { status } = response;
  if (status === 400 || status === 401 || status === 403) {
    return INVALID_CREDENTIALS_MESSAGE;
  }
  return SERVER_ERROR_MESSAGE;
};

// F-07: guarda em `localStorage` só o necessário para operar - o token
// (único segredo; sem cookie httpOnly não há como evitar guardá-lo), o id, o
// nome de exibição e os papéis. O e-mail e `last_login` que o backend devolve
// não são usados por nenhuma tela e ficam de fora.
export const buildSession = ({ token, user, roles }) => {
  const session = {
    uid: user.id,
    id: user.id,
    title: user.title,
    role: roles,
    token,
  };
  // F-28: o backend ainda não manda esta flag; se passar a mandar (em `user`),
  // o superusuário vê todo o menu mesmo com permissões diretas na lista.
  if (user.is_superuser === true) session.is_superuser = true;
  return session;
};

function* loginWithEmailPassword({ payload }) {
  const { user, password } = payload.user;
  const { history } = payload;
  try {
    const response = yield call(loginWithEmailPasswordAsync, user, password);
    const session = buildSession(response);
    setCurrentUser(session);
    yield put(loginUserSuccess(session, session.token));
    history.push(adminRoot);
  } catch (error) {
    yield put(loginUserError(loginErrorMessage(error)));
  }
}

export function* watchLogoutUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOGOUT_USER, logout);
}

const logoutAsync = async (history) => {
  history.push(adminRoot);
};

// Tempo máximo esperado pela revogação no servidor: o operador não pode ficar
// preso na tela por causa de um backend lento.
export const LOGOUT_TIMEOUT_MS = 5000;

// F-07: pede ao backend (`POST /api/auth/logout/`) que apague o token DRF. É
// "best effort": o backend em produção pode ainda não ter a rota (404), estar
// fora do ar ou o token já ter expirado (401) - em qualquer falha a sessão local
// é limpa do mesmo jeito. Precisa rodar ANTES de limpar a sessão, porque o
// interceptor lê o token de `localStorage` a cada chamada.
export const revokeServerSession = async () => {
  try {
    await api.post(LOGOUT_URL, null, { timeout: LOGOUT_TIMEOUT_MS });
    return true;
  } catch (error) {
    return false;
  }
};

export function* logout({ payload }) {
  const { history } = payload;
  if (getCurrentUser()) {
    yield call(revokeServerSession);
  }
  setCurrentUser();
  // F-20: sem header residual para o próximo login; F-15: sem fuso em cache da
  // sessão anterior.
  clearAuthorizationHeader();
  resetInstallationSettingsCache();
  yield call(logoutAsync, history);
}

export default function* rootSaga() {
  yield all([fork(watchLoginUser), fork(watchLogoutUser)]);
}
